import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  XCircle, 
  FileText, 
  Download,
  User,
  Mail,
  Phone,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { useAdmin } from '@hooks/useAdmin';
import { ROUTES, DOCUMENT_STATUS } from '@constants';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const MentorReview = () => {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const { 
    getMentorDetails, 
    approveDocument, 
    rejectDocument, 
    approveMentor,
    rejectMentor,
    loading 
  } = useAdmin();

  const [mentor, setMentor] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchMentorDetails();
  }, [mentorId]);

  const fetchMentorDetails = async () => {
    try {
      const response = await getMentorDetails(mentorId);
      if (response.success) {
        setMentor(response.data);
      }
    } catch (error) {
      toast.error('Failed to load mentor details');
      navigate(ROUTES.ADMIN_DASHBOARD);
    }
  };

  const handleApproveDocument = async (documentId) => {
    setActionLoading(true);
    try {
      const response = await approveDocument(documentId);
      if (response.success) {
        toast.success('Document approved successfully');
        fetchMentorDetails();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to approve document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectDocument = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setActionLoading(true);
    try {
      const response = await rejectDocument(selectedDoc.id, rejectionReason);
      if (response.success) {
        toast.success('Document rejected');
        setShowRejectModal(false);
        setRejectionReason('');
        setSelectedDoc(null);
        fetchMentorDetails();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to reject document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveMentor = async () => {
    if (window.confirm('Are you sure you want to approve this mentor account?')) {
      setActionLoading(true);
      try {
        const response = await approveMentor(mentorId, 'All documents verified and approved');
        if (response.success) {
          toast.success('Mentor account approved successfully');
          fetchMentorDetails();
        }
      } catch (error) {
        toast.error(error.message || 'Failed to approve mentor');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleRejectMentor = async () => {
    const reason = window.prompt('Please provide a reason for rejecting this mentor:');
    if (reason && reason.trim()) {
      setActionLoading(true);
      try {
        const response = await rejectMentor(mentorId, reason);
        if (response.success) {
          toast.success('Mentor account rejected');
          fetchMentorDetails();
        }
      } catch (error) {
        toast.error(error.message || 'Failed to reject mentor');
      } finally {
        setActionLoading(false);
      }
    }
  };

  const getDocumentLabel = (type) => {
    const labels = {
      aadhar: 'Aadhar Card',
      degree_certificate: 'Degree Certificate',
      experience_certificate: 'Experience Certificate',
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: AlertCircle, bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending Review' },
      approved: { icon: CheckCircle, bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      rejected: { icon: XCircle, bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${badge.bg} ${badge.text}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const canApproveMentor = () => {
    if (!mentor?.documents || mentor.documents.length < 3) return false;
    return mentor.documents.every(doc => doc.status === 'approved');
  };

  if (loading || !mentor) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 p-6">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-primary-900 mb-2">
            Mentor Application Review
          </h1>
          <p className="text-primary-600">
            Review and verify mentor documents
          </p>
        </div>

        {/* Mentor Info Card */}
        <Card className="p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {mentor.email?.[0]?.toUpperCase()}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-primary-900">
                  {mentor.email}
                </h2>
                <div className="flex items-center gap-2 mt-2">
                  {getStatusBadge(mentor.adminApproved)}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {mentor.adminApproved !== 'approved' && canApproveMentor() && (
                <Button
                  variant="primary"
                  onClick={handleApproveMentor}
                  disabled={actionLoading}
                  loading={actionLoading}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Mentor
                </Button>
              )}
              {mentor.adminApproved !== 'rejected' && (
                <Button
                  variant="outline"
                  onClick={handleRejectMentor}
                  disabled={actionLoading}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject Mentor
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-primary-500" />
              <div>
                <p className="text-xs text-primary-500">Email</p>
                <p className="text-sm font-medium text-primary-900">{mentor.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-primary-500" />
              <div>
                <p className="text-xs text-primary-500">Phone</p>
                <p className="text-sm font-medium text-primary-900">{mentor.phone || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-primary-500" />
              <div>
                <p className="text-xs text-primary-500">Registered</p>
                <p className="text-sm font-medium text-primary-900">
                  {new Date(mentor.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </Card>

        {/* Documents Section */}
        <div className="mb-6">
          <h3 className="text-xl font-bold text-primary-900 mb-4">
            Verification Documents ({mentor.documents?.length || 0}/3)
          </h3>

          {!mentor.documents || mentor.documents.length === 0 ? (
            <Card className="p-12 text-center">
              <FileText className="w-16 h-16 text-primary-300 mx-auto mb-4" />
              <p className="text-primary-600">No documents uploaded yet</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {mentor.documents.map((doc) => (
                <Card key={doc.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <FileText className="w-6 h-6 text-primary-600" />
                        <div>
                          <h4 className="text-lg font-semibold text-primary-900">
                            {getDocumentLabel(doc.documentType)}
                          </h4>
                          {doc.documentNumber && (
                            <p className="text-sm text-primary-500">
                              Number: {doc.documentNumber}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mb-3">
                        {getStatusBadge(doc.status)}
                        <span className="text-sm text-primary-600">
                          Uploaded: {new Date(doc.createdAt).toLocaleString()}
                        </span>
                      </div>

                      {doc.status === DOCUMENT_STATUS.REJECTED && doc.rejectionReason && (
                        <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-sm font-semibold text-red-800 mb-1">
                            Rejection Reason:
                          </p>
                          <p className="text-sm text-red-700">
                            {doc.rejectionReason}
                          </p>
                        </div>
                      )}

                      {doc.status === DOCUMENT_STATUS.APPROVED && doc.verifiedAt && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-green-700">
                            Verified on {new Date(doc.verifiedAt).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(doc.filePath, '_blank')}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      {doc.status === DOCUMENT_STATUS.PENDING && (
                        <>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleApproveDocument(doc.id)}
                            disabled={actionLoading}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedDoc(doc);
                              setShowRejectModal(true);
                            }}
                            disabled={actionLoading}
                            className="border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Verification Logs */}
        {mentor.verificationLogs && mentor.verificationLogs.length > 0 && (
          <Card className="p-6">
            <h3 className="text-xl font-bold text-primary-900 mb-4">
              Verification History
            </h3>
            <div className="space-y-3">
              {mentor.verificationLogs.map((log) => (
                <div key={log.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-2 h-2 mt-2 rounded-full ${
                    log.action === 'approved' ? 'bg-green-500' : 'bg-red-500'
                  }`} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-primary-900">
                      {log.action === 'approved' ? 'Approved' : 'Rejected'} by {log.admin?.email || 'Admin'}
                    </p>
                    <p className="text-xs text-primary-500 mt-1">
                      {new Date(log.createdAt).toLocaleString()}
                    </p>
                    {log.notes && (
                      <p className="text-sm text-primary-600 mt-2">
                        {log.notes}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-primary-900 mb-4">
              Reject Document
            </h3>
            <p className="text-sm text-primary-600 mb-4">
              Please provide a reason for rejecting this document. This will be shown to the mentor.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-primary-900 focus:outline-none focus:ring-2 focus:ring-primary-400 mb-4"
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setSelectedDoc(null);
                }}
                disabled={actionLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleRejectDocument}
                disabled={actionLoading || !rejectionReason.trim()}
                loading={actionLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                Reject Document
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MentorReview;
